import React from "react";
import moment from "moment";

import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { HealthInsuranceEligibilityClient } from "@/Common/Api/Patient/HealthInsuranceEligibilityClient";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { StatusComponent } from "@/Common/Components/Patient/StatusComponent";
import { ObjectHelper } from "@/Common/Helpers/ObjectHelper";
import { HealthInsuranceEligibility } from "@/Common/Models/Patient/HealthInsuranceEligibility/HealthInsuranceEligibility";
import { HealthInsuranceEligibilityStatus } from "@/Common/Models/Patient/HealthInsuranceEligibility/HealthInsuranceEligibilityStatus";
import { Patient } from "@/Common/Models/Patient/Patient";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { BritishColumbiaMspCoverageHelper } from "./BritishColumbiaMspCoverageHelper";
import { MedicalServicesPlanCoverageInformationPopup } from "./MedicalServicesPlanCoverageInfomationPopup";

interface IBritishColumbiaPatientBannerMspCoverageProps {
    patient: Patient;
    dateOfBirth: Date; // needed to update the component after editing the patient's date of birth
}

class BritishColumbiaPatientBannerMspCoverageState {
    public showMspCoverageInformationPopup: boolean = false;
    public mspEligibility: HealthInsuranceEligibility = new HealthInsuranceEligibility();
    public clickPositionX: number = 0;
    public clickPositionY: number = 0;

    public constructor(fields?: Partial<BritishColumbiaPatientBannerMspCoverageState>) {
        Object.assign(this, fields);
    }
}

class BritishColumbiaPatientBannerMspCoverage extends BaseReactComponent<IBritishColumbiaPatientBannerMspCoverageProps, BritishColumbiaPatientBannerMspCoverageState> {
    private readonly mspCoverageHelper = new BritishColumbiaMspCoverageHelper();

    constructor(props: IBritishColumbiaPatientBannerMspCoverageProps) {
        super(props);
        this.state = new BritishColumbiaPatientBannerMspCoverageState();
    }

    public componentDidMount(): void {
        this.initState(this.props);
    }

    public componentDidUpdate(prevProps: IBritishColumbiaPatientBannerMspCoverageProps): void {
        if (this.props.dateOfBirth !== prevProps.dateOfBirth
            || this.props.patient.IndividualResponse?.Contact_id !== prevProps.patient.IndividualResponse?.Contact_id
            || !ObjectHelper.equals(this.props.patient.HealthCards, prevProps.patient.HealthCards)
        ) {
            this.initState(this.props);
        }
    }

    private async initState(props: IBritishColumbiaPatientBannerMspCoverageProps): Promise<void> {
        let mspEligibility: HealthInsuranceEligibility = null;

        let dateOfBirth = DateOnly.parseUnlessNull(props.dateOfBirth);

        const isNewBorn = moment().diff(moment(props.dateOfBirth), "days") <= 60;
        if (isNewBorn) {
            const representatives: LinkedContact[] = props.patient.Relationships?.filter(x => x.IsRepresentative) ?? [];
            dateOfBirth = DateOnly.parseUnlessNull(representatives.firstOrDefault()?.Individual.DateOfBirth);
        }

        const mspCardNumber = this.getMspCardNumber(props.patient);

        if (!dateOfBirth || !mspCardNumber || !this.mspCoverageHelper.isPersonalHealthNumberValid(props.patient.IndividualResponse, mspCardNumber)) {
            mspEligibility = new HealthInsuranceEligibility({ Eligibility: HealthInsuranceEligibilityStatus.MissingInformation });
        }
        else {
            let patientHealthCardProvinceLookupId = props.patient.HealthCards.firstOrDefault()?.ProvinceLookupId;
            if (!patientHealthCardProvinceLookupId)
                patientHealthCardProvinceLookupId = cacheStore.GetLookupIdForProvince(CanadaState.BritishColumbia);

            mspEligibility = await HealthInsuranceEligibilityClient.getHealthInsuranceEligibility(mspCardNumber, dateOfBirth, patientHealthCardProvinceLookupId);
        }

        this.mutateState(s => {
            s.mspEligibility = mspEligibility;
            s.showMspCoverageInformationPopup = false;
            s.clickPositionX = 0;
            s.clickPositionY = 0;
        });
    }

    private getMspCardNumber(patient: Patient): string {
        const patientHealthCard = patient.HealthCards
            ?.firstOrDefault(p => p.ProvinceLookupId === cacheStore.GetLookupIdForProvince(CanadaState.BritishColumbia))
            ?.Number;

        return patientHealthCard ?? patient.GetRepresentativeNAM();
    }

    private onMspCoverageClick(event: React.MouseEvent): void {
        event.persist();
        this.mutateState(s => {
            s.clickPositionX = event.clientX;
            s.clickPositionY = event.clientY;
            s.showMspCoverageInformationPopup = !s.showMspCoverageInformationPopup;
        });
    }

    private shouldShowMspPopup(): boolean {
        return this.state.showMspCoverageInformationPopup
            && this.mspCoverageHelper.mspEligibilityHasExtraInformation(
                this.state.mspEligibility,
                this.props.patient.IndividualResponse,
                this.getMspCardNumber(this.props.patient)
            );
    }

    public render(): JSX.Element {
        const patient = this.props.patient;
        const individual = patient.IndividualResponse;
        const mspEligibility = this.state.mspEligibility;

        let statusInfo = this.props.patient.GetFormattedIntervenorOnFile();
        if (!statusInfo || cacheStore.ClinicSettings.EnablePopulationBasedFunding) {
            statusInfo = cacheStore.GetString("MedicalServicesPlanAbbrevLabel");
        }

        return (
            <div>
                <StatusComponent
                    status={this.mspCoverageHelper.getEligibilityStatus(mspEligibility)}
                    onClickStatus={this.mspCoverageHelper.mspEligibilityHasExtraInformation(mspEligibility, individual, this.getMspCardNumber(patient))
                        ? event => this.onMspCoverageClick(event)
                        : null
                    }
                    statusInfo={statusInfo}
                    isBritishColumbia
                />

                {this.shouldShowMspPopup() && (
                    <MedicalServicesPlanCoverageInformationPopup
                        mspEligibility={this.state.mspEligibility}
                        xPosition={this.state.clickPositionX}
                        yPosition={this.state.clickPositionY}
                        onCloseCallback={() => this.mutateState(s => s.showMspCoverageInformationPopup = false)}
                        isMissingDateOfBirth={!this.props.dateOfBirth}
                        isMissingPHN={IsNullOrWhiteSpace(this.getMspCardNumber(patient))}
                        isPersonalHealthNumberInvalid={!this.mspCoverageHelper.isPersonalHealthNumberValid(
                            individual,
                            this.getMspCardNumber(patient)
                        )}
                    />
                )}
            </div>
        );
    }
}

export { BritishColumbiaPatientBannerMspCoverage };
